import { applyIncrement, applyMove, applyRefill, createInitialState } from "./engine";
import { expireFlow, getAverageFlowChainLength, getNextFlowCount } from "./flow";
import { resolveMove } from "./helpers";
import { shiftIncrementWindows, syncIncrementState } from "./increment";
import { syncRefillState } from "./refill";
import { applyScoreEvents, createScoreEvents, getAwardedPoints } from "./scoring";
import type {
  GameAction,
  GameState,
  ReducerResult,
  TelemetryEvent,
  TileGenerator
} from "./types";

function roundStartedEvent(state: GameState): TelemetryEvent {
  return {
    name: "round_started",
    payload: {
      roundNumber: state.roundNumber,
      roundsCleared: state.roundsCleared
    }
  };
}

function reconcileMechanicProgression(
  state: GameState,
  now: number,
  generator: TileGenerator,
  activatedByClear: boolean
): GameState {
  if (state.gameOver) {
    return state;
  }

  const progression = {
    roundNumber: state.roundNumber,
    score: state.score
  };
  const nextRefill = syncRefillState({
    refill: state.refill,
    board: state.board,
    flow: state.flow,
    now,
    progression,
    activatedByClear: activatedByClear && !state.refill.timerVisible
  });
  const nextIncrement = syncIncrementState({
    increment: state.increment,
    board: state.board,
    now,
    progression,
    randomSource: generator.nextRandom
  });

  if (nextRefill === state.refill && nextIncrement === state.increment) {
    return state;
  }

  return {
    ...state,
    refill: nextRefill,
    increment: nextIncrement
  };
}

export function reduceGame(
  state: GameState,
  action: GameAction,
  generator: TileGenerator
): ReducerResult {
  if (action.type === "restart_run") {
    const nextState = createInitialState(
      generator,
      state.bestRound,
      state.bestScore,
      state.bestMaxFlow,
      action.initialDeal ?? null
    );
    return {
      state: nextState,
      events: [roundStartedEvent(nextState)],
      scoreEvents: []
    };
  }

  if (action.type === "select_hand_tile") {
    if (state.gameOver || state.hand[action.handIndex] === undefined) {
      return { state, events: [], scoreEvents: [] };
    }

    return {
      state: { ...state, selectedHandIndex: action.handIndex },
      events: [],
      scoreEvents: []
    };
  }

  if (action.type === "shift_time_windows") {
    if (
      action.deltaMs <= 0
    ) {
      return { state, events: [], scoreEvents: [] };
    }

    return {
      state: {
        ...state,
        flow: {
          ...state.flow,
          lastMoveAt:
            state.flow.lastMoveAt === null ? null : state.flow.lastMoveAt + action.deltaMs,
          expiresAt: state.flow.expiresAt === null ? null : state.flow.expiresAt + action.deltaMs
        },
        refill: {
          ...state.refill,
          expiresAt:
            state.refill.expiresAt === null ? null : state.refill.expiresAt + action.deltaMs
        },
        increment: shiftIncrementWindows(state.increment, action.deltaMs)
      },
      events: [],
      scoreEvents: []
    };
  }

  if (action.type === "flow_window_expired") {
    const nextFlowState = expireFlow(state.flow, action.expiresAt);
    if (nextFlowState === state.flow) {
      return { state, events: [], scoreEvents: [] };
    }

    return {
      state: {
        ...state,
        flow: nextFlowState,
        refill: syncRefillState({
          refill: state.refill,
          board: state.board,
          flow: nextFlowState,
          now: action.expiresAt,
          progression: {
            roundNumber: state.roundNumber,
            score: state.score
          }
        })
      },
      events: [],
      scoreEvents: []
    };
  }

  if (action.type === "refill_window_expired") {
    const nextState = applyRefill(state, action.expiresAt, generator);
    if (nextState === state) {
      return { state, events: [], scoreEvents: [] };
    }

    return {
      state: nextState,
      events: [],
      scoreEvents: []
    };
  }

  if (action.type === "increment_window_expired") {
    const nextState = applyIncrement(state, action.laneIndex, action.expiresAt, generator);
    if (nextState === state) {
      return { state, events: [], scoreEvents: [] };
    }

    return {
      state: nextState,
      events: [],
      scoreEvents: []
    };
  }

  if (state.gameOver) {
    return { state, events: [], scoreEvents: [] };
  }

  if (state.selectedHandIndex === null) {
    return { state, events: [], scoreEvents: [] };
  }

  const handTile = state.hand[state.selectedHandIndex];
  const selectedTile = state.board[action.boardIndex];
  if (handTile === undefined || selectedTile === undefined) {
    return { state, events: [], scoreEvents: [] };
  }

  const resolution = resolveMove(handTile, selectedTile.value);

  if (resolution.outcome === "illegal") {
    return {
      state,
      events: [
        {
          name: "illegal_move_attempted",
          payload: {
            handIndex: state.selectedHandIndex,
            handTile,
            boardIndex: action.boardIndex,
            boardTile: selectedTile.value,
            sum: resolution.sum
          }
        }
      ],
      scoreEvents: []
    };
  }

  const nextFlowCount = getNextFlowCount(state.flow, action.playedAt);
  const stateAfterMove = applyMove(
    state,
    state.selectedHandIndex,
    action.boardIndex,
    action.playedAt,
    generator
  );
  const scoreEvents = createScoreEvents({
    moveOutcome: resolution.outcome,
    flow: state.flow,
    playedAt: action.playedAt,
    boardIndex: action.boardIndex,
    roundCleared: stateAfterMove.roundsCleared > state.roundsCleared,
    clearedTile: resolution.clearsTile ? selectedTile : null
  });
  const nextState = reconcileMechanicProgression(
    applyScoreEvents(stateAfterMove, scoreEvents),
    action.playedAt,
    generator,
    resolution.clearsTile
  );
  const awardedPoints = getAwardedPoints(scoreEvents);
  const events: TelemetryEvent[] = [
    {
      name: "move_applied",
      payload: {
        handIndex: state.selectedHandIndex,
        handTile,
        boardIndex: action.boardIndex,
        boardTile: selectedTile.value,
        sum: resolution.sum,
        outcome: resolution.outcome,
        flowCount: nextFlowCount,
        scoreAwarded: awardedPoints,
        score: nextState.score
      }
    }
  ];
  let runningScore = state.score;

  for (const scoreEvent of scoreEvents) {
    runningScore += scoreEvent.awardedPoints;
    events.push({
      name: "score_awarded",
      payload: {
        type: scoreEvent.type,
        basePoints: scoreEvent.basePoints,
        flowMultiplier: scoreEvent.flowMultiplier,
        tileMultiplier: scoreEvent.tileMultiplier,
        multiplier: scoreEvent.multiplier,
        awardedPoints: scoreEvent.awardedPoints,
        origin: scoreEvent.origin,
        score: runningScore,
        ...(scoreEvent.boardIndex === null ? {} : { boardIndex: scoreEvent.boardIndex })
      }
    });
  }

  if (nextState.roundsCleared > state.roundsCleared) {
    events.push({
      name: "round_cleared",
      payload: {
        roundsCleared: nextState.roundsCleared,
        score: nextState.score
      }
    });
    events.push(roundStartedEvent(nextState));
  }

  if (nextState.gameOver && !state.gameOver) {
    events.push({
      name: "run_ended",
      payload: {
        roundsCleared: nextState.roundsCleared,
        roundReached: nextState.roundNumber,
        bestRound: nextState.bestRound,
        score: nextState.score,
        bestScore: nextState.bestScore,
        maxFlow: nextState.flow.maxCount,
        bestMaxFlow: nextState.bestMaxFlow,
        averageFlowChainLength: getAverageFlowChainLength(nextState.flow),
        flowChainsStarted: nextState.flow.chainsStarted
      }
    });
  }

  return { state: nextState, events, scoreEvents };
}
